/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { 
  signIn, 
  getCurrentUser, 
  fetchAuthSession,
  signInWithRedirect,
  resetPassword,
  confirmResetPassword
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { checkUserAuthorization } from '../services/authService';
import config from '../config/config';

// Configure Amplify
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: config.cognito.userPoolId,
      userPoolClientId: config.cognito.userPoolWebClientId,
      userPoolEndpoint: `https://cognito-idp.${config.cognito.region}.amazonaws.com`,
      loginWith: {
        oauth: {
          domain: config.cognito.oauth.domain,
          scopes: config.cognito.oauth.scope,
          redirectSignIn: [config.cognito.oauth.redirectSignIn],
          redirectSignOut: [config.cognito.oauth.redirectSignOut],
          responseType: config.cognito.oauth.responseType
        }
      }
    }
  }
});

// Create Auth Context
const AuthContext = createContext(null);

/**
 * AuthProvider Component
 * 
 * Provides authentication state and methods to the entire application.
 * Supports both Cognito-native authentication and future SSO integration.
 * 
 * Architecture:
 * - Cognito-native: Username/password authentication
 * - SSO-ready: Structured to support SAML/OIDC without refactoring
 * - Authorization: Separate check (email in database) after authentication
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [pendingChallenge, setPendingChallenge] = useState(null); // Store challenge state

  /**
   * Check if user is authenticated on mount
   */
  useEffect(() => {
    checkAuthStatus();

    // Listen for SSO redirect completion
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signInWithRedirect') {
        // SSO redirect completed — clear the signed-out flag and re-check auth
        sessionStorage.removeItem('justSignedOut');
        checkAuthStatus();
      }
      if (payload.event === 'signInWithRedirect_failure') {
        setLoading(false);
        setError('SSO sign-in failed. Please try again or contact your administrator.');
      }
    });

    return () => unsubscribe();
  }, []);

  /**
   * Check current authentication status
   */
  const checkAuthStatus = async () => {
    try {
      setLoading(true);
      setError(null);

      // If the user just signed out, don't attempt to re-establish a session.
      // Amplify may still be able to refresh tokens from a cached Cognito session
      // (especially with SSO where Azure AD silently re-authenticates).
      // Only check sessionStorage — the URL param is handled by Login.jsx which
      // sets sessionStorage. This avoids the URL param blocking re-login after
      // the user submits credentials (the URL still has ?signed-out=true).
      if (sessionStorage.getItem('justSignedOut') === 'true') {
        setUser(null);
        setIsAuthenticated(false);
        setIsAuthorized(false);
        setLoading(false);
        return;
      }

      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();

      if (currentUser && session) {
        // Get user attributes from the ID token
        const idToken = session.tokens?.idToken;
        const userEmail = idToken?.payload?.email || currentUser.username;
        const userName = idToken?.payload?.name || userEmail;
        
        // Determine role: check Cognito groups first, then custom:role attribute
        const cognitoGroups = idToken?.payload?.['cognito:groups'] || [];
        let userRole = null;
        if (cognitoGroups.includes('admin')) {
          userRole = 'admin';
        } else if (cognitoGroups.includes('user')) {
          userRole = 'manager';
        } else if (cognitoGroups.includes('readonly')) {
          userRole = 'viewer';
        } else {
          userRole = idToken?.payload?.['custom:role'] || 'viewer';
        }
        
        setUser({
          username: currentUser.username,
          email: userEmail,
          name: userName,
          role: userRole,
          attributes: currentUser.attributes || {}
        });
        setIsAuthenticated(true);
        setPendingChallenge(null);

        // Check authorization: verify user was pre-created by admin
        // SSO users (federated identities) must have a matching admin-created account
        const isSSOUser = currentUser.username?.startsWith('tui-sso_') || currentUser.username?.startsWith('TUI-SSO_');
        
        if (isSSOUser) {
          try {
            const authResult = await checkUserAuthorization();
            if (authResult.authorized) {
              // Use the role from the pre-created user record
              setUser(prev => ({
                ...prev,
                role: authResult.role || prev.role,
                name: authResult.name || prev.name
              }));
              setIsAuthorized(true);
            } else {
              setIsAuthorized(false);
              setError('Your account has not been set up yet. Please contact your administrator to be added to TUI ReportHub.');
            }
          } catch {
            setIsAuthorized(false);
            setError('Unable to verify authorization. Please try again.');
          }
        } else {
          // Non-SSO users (email/password) are already pre-created by admin
          setIsAuthorized(true);
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setIsAuthorized(false);
      }
    } catch (err) {
      console.log('Not authenticated:', err.message);
      setUser(null);
      setIsAuthenticated(false);
      setIsAuthorized(false);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Login with username and password (Cognito-native)
   * 
   * @param {string} username - User's email
   * @param {string} password - User's password
   * @returns {Promise<object>} User object
   */
  const login = async (username, password) => {
    try {
      setLoading(true);
      setError(null);

      // Clear the signed-out flag — user is actively signing in
      sessionStorage.removeItem('justSignedOut');

      const result = await signIn({ 
        username, 
        password 
      });

      if (result.isSignedIn) {
        await checkAuthStatus();
        return { success: true };
      }

      // Handle next steps (MFA, password change, etc.)
      // IMPORTANT: Don't set isAuthenticated here - user is not fully signed in yet
      if (result.nextStep) {
        // Store the challenge in context so it survives component remounts
        const challengeData = {
          type: result.nextStep.signInStep,
          message: getNextStepMessage(result.nextStep.signInStep),
          username: username // Store username for password change
        };
        setPendingChallenge(challengeData);
        
        // Return the challenge type so the UI can handle it
        return { 
          success: false, 
          nextStep: result.nextStep.signInStep,
          challengeName: result.nextStep.signInStep,
          message: challengeData.message
        };
      }

      return { success: false, message: 'Authentication failed' };
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get user-friendly message for next step
   */
  const getNextStepMessage = (stepType) => {
    switch (stepType) {
      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
        return 'You must change your temporary password';
      case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
        return 'Please enter the SMS code sent to your phone';
      case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
        return 'Please enter your authenticator code';
      default:
        return 'Additional authentication required';
    }
  };

  /**
   * Complete new password challenge
   * 
   * @param {string} newPassword - New password
   * @param {object} userAttributes - Optional user attributes (e.g., name)
   * @returns {Promise<object>} Result object
   */
  const completeNewPassword = async (newPassword, userAttributes = {}) => {
    try {
      setLoading(true);
      setError(null);

      // Import confirmSignIn dynamically
      const { confirmSignIn } = await import('aws-amplify/auth');
      
      // Cognito requires 'name' attribute if it's marked as required in the User Pool
      // Use the username (email) from the pending challenge, or provided name
      const username = pendingChallenge?.username || 'User';
      const attributes = {
        name: userAttributes.name || username,
        ...userAttributes
      };
      
      const result = await confirmSignIn({
        challengeResponse: newPassword,
        options: {
          userAttributes: attributes
        }
      });

      if (result.isSignedIn) {
        setPendingChallenge(null); // Clear the challenge
        await checkAuthStatus();
        return { success: true };
      }

      return { success: false, message: 'Password change failed' };
    } catch (err) {
      console.error('Password change error:', err);
      setError(err.message || 'Password change failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Login with SSO (Future implementation)
   * 
   * This method is structured for future SSO integration.
   * When SSO is configured in Cognito, this will redirect to the SSO provider.
   */
  const loginWithSSO = async () => {
    try {
      setLoading(true);
      setError(null);
      // Clear the signed-out flag — user is actively signing in
      sessionStorage.removeItem('justSignedOut');
      await signInWithRedirect({ provider: { custom: 'TUI-SSO' } });
    } catch (err) {
      console.error('SSO login error:', err);
      setError(err.message || 'SSO login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logout user
   * Redirects directly to the Cognito hosted UI logout endpoint to clear
   * the Cognito session cookie and prevent SSO silent re-authentication.
   * 
   * We skip Amplify's signOut() entirely because:
   * 1. signOut({ global: true }) can trigger unwanted OAuth redirects on failure
   * 2. The Cognito logout endpoint handles session cleanup server-side
   * 3. Local tokens are cleared by Amplify when the user lands back on the app
   *    without a valid session
   */
  const logout = async () => {
    try {
      setUser(null);
      setIsAuthenticated(false);
      setIsAuthorized(false);

      // Clear Amplify auth tokens from localStorage selectively.
      // Do NOT call localStorage.clear() — Amplify stores its OAuth configuration
      // (redirect_uri, scopes, etc.) in localStorage too. Wiping everything causes
      // "redirect_uri is not present" errors when the user tries to sign back in.
      try {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (
            key.startsWith('CognitoIdentityServiceProvider') ||
            key.includes('idToken') ||
            key.includes('accessToken') ||
            key.includes('refreshToken') ||
            key.includes('LastAuthUser') ||
            key.includes('clockDrift')
          )) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
      } catch { /* ignore */ }

      // Set the signed-out flag in sessionStorage BEFORE redirecting.
      // This must survive the redirect so PublicRoute and Login can detect it
      // on the next page load and prevent auto-SSO re-authentication.
      // Note: we intentionally do NOT clear sessionStorage here.
      try {
        sessionStorage.setItem('justSignedOut', 'true');
      } catch { /* ignore */ }

      // Redirect to Cognito hosted UI logout endpoint.
      // This clears the Cognito session cookie so SSO doesn't silently re-auth.
      // Cognito then redirects to logout_uri (/login?signed-out=true).
      //
      // Skip the Cognito redirect for localhost — localhost isn't registered
      // as a logout_url in the Cognito client, so Cognito rejects it with
      // "redirect_uri is not present". For local dev, just redirect directly.
      const redirectSignOut = config.cognito.oauth.redirectSignOut;
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

      if (isLocalhost) {
        window.location.href = '/login?signed-out=true';
        return;
      }

      const domain = config.cognito.oauth.domain;
      const clientId = config.cognito.userPoolWebClientId;
      const logoutUri = encodeURIComponent(redirectSignOut);
      const cognitoLogoutUrl = `https://${domain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`;

      window.location.href = cognitoLogoutUrl;
    } catch (err) {
      console.error('Logout error:', err);
      window.location.href = '/login?signed-out=true';
    }
  };

  /**
   * Get current user details
   */
  const getUserDetails = async () => {
    try {
      const currentUser = await getCurrentUser();
      return currentUser;
    } catch (err) {
      console.error('Get user details error:', err);
      return null;
    }
  };

  /**
   * Get current auth session (includes tokens)
   */
  const getSession = async () => {
    try {
      const session = await fetchAuthSession();
      return session;
    } catch (err) {
      console.error('Get session error:', err);
      return null;
    }
  };

  /**
   * Get access token for API calls
   */
  const getAccessToken = async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString() || null;
    } catch (err) {
      console.error('Get access token error:', err);
      return null;
    }
  };

  /**
   * Check if user is authorized (email in database)
   * 
   * TODO: Implement actual authorization check against backend API
   * This should verify that the authenticated user's email exists in the database
   */
  const checkAuthorization = async () => {
    try {
      const result = await checkUserAuthorization();
      return result.authorized;
    } catch (err) {
      console.error('Authorization check error:', err);
      return false;
    }
  };

  /**
   * Initiate forgot password flow — sends a verification code to the user's email
   */
  const forgotPassword = async (username) => {
    try {
      const result = await resetPassword({ username });
      return { 
        success: true, 
        codeDeliveryDetails: result.nextStep?.codeDeliveryDetails 
      };
    } catch (err) {
      console.error('Forgot password error:', err);
      throw err;
    }
  };

  /**
   * Confirm forgot password — verify code and set new password
   */
  const confirmForgotPassword = async (username, confirmationCode, newPassword) => {
    try {
      await confirmResetPassword({ username, confirmationCode, newPassword });
      return { success: true };
    } catch (err) {
      console.error('Confirm forgot password error:', err);
      throw err;
    }
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated,
    isAuthorized,
    pendingChallenge, // Expose challenge state
    login,
    loginWithSSO,
    logout,
    completeNewPassword,
    forgotPassword,
    confirmForgotPassword,
    getUserDetails,
    getSession,
    getAccessToken,
    checkAuthorization,
    refreshAuth: checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * useAuth Hook
 * 
 * Custom hook to access authentication context
 * 
 * @returns {object} Authentication context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}

export default AuthContext;
